import { FC, useEffect, useRef } from 'react';

import { CampaignType } from 'utils/constants/campaign';

import { Card, Cell, CellText, Checkbox, Text, Tooltip, VSpacing, VSpacingContainer } from '@hh.ru/magritte-ui';
import { QuestionCircleOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';

import { selectIsAdvertisement, setClassificationCodeId, setIsAdvertisement } from 'store/models/createBanner';
import { canExplicitlyChoseCampaignType } from 'store/models/user';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    label: 'adCreate.adForm.advertisementBannerCheckbox.label',
    tooltip: {
        part1: 'adCreate.adForm.advertisementBannerCheckbox.tooltip.part1',
        part2: 'adCreate.adForm.advertisementBannerCheckbox.tooltip.part2',
        part3: 'adCreate.adForm.advertisementBannerCheckbox.tooltip.part3',
    },
};

const ADVERTISEMENT_BANNER_CLASSIFICATION_CODE_ID = '6.1.1';

export const AdvertisementBannerCheckbox: FC = () => {
    const canMarkBannerAsAdvertisement = useSelector(canExplicitlyChoseCampaignType);
    const isAdvertisement = useSelector(selectIsAdvertisement);
    const campaignType = useSelector((state) => state.createCampaign.campaignType);

    const [isTooltipVisible, setIsTooltipVisibleOn, setIsTooltipVisibleOff] = useOnOffState(false);

    const trls = useTrls();
    const dispatch = useDispatch();

    const activatorRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        campaignType === CampaignType.Information &&
            // если баннер рекламный, для него нужно указать код ККТУ, при этом давать выбирать его пользователю нет необходимости
            dispatch(setClassificationCodeId(isAdvertisement ? ADVERTISEMENT_BANNER_CLASSIFICATION_CODE_ID : null));
    }, [dispatch, isAdvertisement, campaignType]);

    if (!canMarkBannerAsAdvertisement || campaignType === CampaignType.Advertising) {
        return null;
    }

    return (
        <>
            <VSpacing default={40} />
            <Card padding={16} borderRadius={12} stretched borderWidth="default">
                <Cell
                    left={
                        <Checkbox
                            checked={isAdvertisement}
                            onChange={() => dispatch(setIsAdvertisement(!isAdvertisement))}
                            data-qa="advertisement-banner-checkbox"
                        />
                    }
                    right={
                        <QuestionCircleOutlinedSize24
                            initialColor="secondary"
                            ref={activatorRef}
                            onClick={setIsTooltipVisibleOn}
                        />
                    }
                >
                    <CellText>{trls(TrlKeys.label)}</CellText>
                </Cell>
            </Card>
            <Tooltip
                visible={isTooltipVisible}
                onClose={setIsTooltipVisibleOff}
                placement="right-center"
                activatorRef={activatorRef}
            >
                <VSpacingContainer default={8}>
                    <Text>{trls(TrlKeys.tooltip.part1)}</Text>
                    <Text>{trls(TrlKeys.tooltip.part2)}</Text>
                    <Text>{trls(TrlKeys.tooltip.part3)}</Text>
                </VSpacingContainer>
            </Tooltip>
        </>
    );
};
